import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import {
  generatePlaylistOutline,
  narratePlaylist,
  publishUpdatedForPlaylist,
  transcribePlaylist,
  updatePlaylistForRadio,
} from '@store/thunk/playlist.thunk'
import { poolNewTask } from '@store/actions/task.actions'
import {
  PlaylistModel,
  setPlaylistInProgress,
} from '@store/slices/playlist.slice'
import {
  AISlotModel,
  selectAiSlotsByIds,
  setAiSlotInProgress,
} from '@store/slices/aiSlot.slice'
import { TaskModel } from '@store/slices/task.slice'
import { RootState, store } from '@store/store'
import {
  NarrationModel,
  selectNarrationsByIds,
  setNarrationInProgress,
} from '@store/slices/narration.slice'
import { setAiMetadataInProgress } from '@store/slices/aiMeta.slice'

export const handleNarratePlaylist = async (
  playlist: PlaylistModel
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const playlistSlots: AISlotModel[] = selectAiSlotsByIds(
    state,
    playlist.aiSlots
  )
  const playlistNarrations = playlistSlots.flatMap(
    (aiSlot) => aiSlot.narrations
  )
  return dispatch(narratePlaylist({ playlistId: playlist.id }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Narace playlistu ${playlist.id}`,
        relatedLink: `/playlist/${playlist.id}`,
        relatedObjects: [
          {
            entityName: 'aiSlot',
            entityIds: [...playlist.aiSlots],
          },
          {
            entityName: 'playlist',
            entityIds: [playlist.id],
          },
          {
            entityName: 'narration',
            entityIds: [...playlistNarrations],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setAiSlotInProgress(playlist.aiSlots))
      dispatch(setNarrationInProgress(playlistNarrations))
      return taskId
    })
}

export const handleTranscribePlaylist = async (
  playlist: PlaylistModel
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const playlistSlots: AISlotModel[] = selectAiSlotsByIds(
    state,
    playlist.aiSlots
  )
  const playlistNarrations = playlistSlots.flatMap(
    (aiSlot) => aiSlot.narrations
  )
  return dispatch(transcribePlaylist({ playlistId: playlist.id }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Transkripce dialogů playlistu ${playlist.id}`,
        relatedLink: `/playlist/${playlist.id}`,
        relatedObjects: [
          {
            entityName: 'aiSlot',
            entityIds: [...playlist.aiSlots],
          },
          {
            entityName: 'playlist',
            entityIds: [playlist.id],
          },
          {
            entityName: 'narration',
            entityIds: [...playlistNarrations],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setAiSlotInProgress(playlist.aiSlots))
      dispatch(setNarrationInProgress(playlistNarrations))
      return taskId
    })
}

export const handlePublishUpdatedForPlaylist = async (
  playlist: PlaylistModel
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const playlistSlots: AISlotModel[] = selectAiSlotsByIds(
    state,
    playlist.aiSlots
  )
  const playlistNarrations: NarrationModel[] = selectNarrationsByIds(
    state,
    playlistSlots.flatMap((aiSlot) => aiSlot.narrations)
  )
  const affectedNarrations: NarrationModel[] = playlistNarrations.filter(
    (narration: NarrationModel) =>
      narration.audioCreated &&
      narration.audioCreated &&
      narration.uploaded &&
      new Date(narration.audioCreated) > new Date(narration.uploaded)
  )
  const affectedNarrationsIds: EntityId[] = affectedNarrations.map(
    (narration) => narration.id
  )
  const affectedAiSlots: EntityId[] = affectedNarrations.map(
    (narration) => narration.aiSlot
  )
  return dispatch(publishUpdatedForPlaylist(playlist.id))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Publikace aktualizovaných audií playlistu ${playlist.id}`,
        relatedLink: `/playlist/${playlist.id}/published`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [...affectedNarrationsIds],
          },
          {
            entityName: 'playlist',
            entityIds: [playlist.id],
          },
          {
            entityName: 'aiSlot',
            entityIds: [...affectedAiSlots],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(affectedNarrationsIds))
      return taskId
    })
}

export const handleGeneratePlaylistOutline = async (
  playlist: PlaylistModel
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const playlistSlots: AISlotModel[] = selectAiSlotsByIds(
    state,
    playlist.aiSlots
  )
  const playlistAiMetadataIds: EntityId[] = playlistSlots.flatMap((aiSlot) =>
    aiSlot.aiEntryMetadata ? [aiSlot.aiEntryMetadata] : []
  )

  return dispatch(generatePlaylistOutline({ playlistId: playlist.id }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Generování návrhu playlistu ${playlist.id}`,
        relatedLink: `/playlist/${playlist.id}/outline`,
        relatedObjects: [
          {
            entityName: 'aiMetadata',
            entityIds: [...playlistAiMetadataIds],
          },
          {
            entityName: 'playlist',
            entityIds: [playlist.id],
          },
          {
            entityName: 'aiSlot',
            entityIds: [...playlist.aiSlots],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setAiMetadataInProgress(playlistAiMetadataIds))
      dispatch(setPlaylistInProgress(playlist.id))
      dispatch(setAiSlotInProgress(playlist.aiSlots))
      return taskId
    })
}

export const handleUpdatePlaylistFromRadio = async (
  playlist: PlaylistModel
): Promise<EntityId> => {
  const dispatch = store.dispatch
  return dispatch(updatePlaylistForRadio({ playlistId: playlist.id }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Aktualizace playlist ${playlist.id} z Rádia`,
        relatedLink: `/playlist/${playlist.id}`,
        relatedObjects: [
          {
            entityName: 'playlist',
            entityIds: [playlist.id],
          },
          {
            entityName: 'aiSlot',
            entityIds: [...playlist.aiSlots],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setPlaylistInProgress(playlist.id))
      dispatch(setAiSlotInProgress(playlist.aiSlots))
      return taskId
    })
}
